<template>
  <div class="dropzone-wrapper">
    <div
      :id="id"
      ref="dropzoneElement"
      class="dropzone"
    />
    <div
      v-if="!inlinePreviews"
      :class="`dropzone-previews-container-${_uid}`"
    />
    <div ref="previewTemplate">
      <div
        class="flex justify-between dz-item"
        :class="{ 'mb-4': includeBottomPreviewMargin }"
      >
        <div class="dz-image">
          <div>
            <span data-dz-uploadprogress />
          </div>
          <img
            class="w-32 h-32 bg-grey-lighter border-none rounded-lg"
            data-dz-thumbnail
          >
        </div>
        <div class="text-left ml-4 mr-auto flex flex-col justify-around">
          <div>
            <div class="inline-block">
              <div class="bg-red dz-error-message p-2 rounded-lg text-sm text-white">
                <span data-dz-errormessage />
              </div>
              <div
                class="bg-green dz-success-message p-2 rounded-lg text-sm text-white"
                v-text="'Uploaded'"
              />
              <div
                class="bg-grey dz-uploading-message p-2 rounded-lg text-sm text-white"
                v-text="'Uploading...'"
              />
            </div>
            <div class="my-2">
              <span data-dz-name />
              <br>
              <span data-dz-size />
            </div>
            <a
              class="remove-link"
              data-dz-remove
              v-text="'Remove file'"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
import Dropzone from 'dropzone'

Dropzone.autoDiscover = false

export default {
  name: 'BaseDropzone',
  props: {
    inlinePreviews: {
      type: Boolean,
      default: false,
      required: false,
    },
    id: {
      type: String,
      required: false,
      default: 'dropzone',
    },
    options: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isUploading: false,
      queuedCount: 0,
      hasBeenMounted: false,
    }
  },
  computed: {
    dropzoneSettings() {
      const defaultValues = {
        thumbnailWidth: 120,
        thumbnailHeight: 120,
        addRemoveLinks: false,
        dictDefaultMessage: 'Drop files or click to upload',
        previewTemplate: this.previewTemplate,
        url: 'here',
        maxFilesize: 15,
        autoProcessQueue: true,
        previewsContainer: document.querySelector('.dropzone-previews-container-' + this._uid),
        resizeWidth: 1500,
        resizeHeight: 1500,
        // acceptedFiles:
        //     'image/*,.pdf,.doc,.docx,.pages,.numbers,.xls,.ppt,.txt,.xml,.csv,.svg',
      }

      Object.keys(this.options).forEach((key) => {
        defaultValues[key] = this.options[key]
      }, this)

      return defaultValues
    },
    includeBottomPreviewMargin() {
      return false
    },
  },
  mounted() {
    if (this.hasBeenMounted) {
      return true
    }

    const previewNode = this.$refs.previewTemplate

    this.previewTemplate = previewNode.innerHTML

    previewNode.parentNode.removeChild(previewNode)

    this.hasBeenMounted = true

    this.dropzone = new Dropzone(this.$refs.dropzoneElement, this.dropzoneSettings)

    const vm = this

    this.dropzone.on('addedfile', function (file) {
      vm.$emit('dropzone-file-added', file)
    })
    this.dropzone.on('addedfiles', function (files) {
      vm.$emit('dropzone-files-added', files)
    })
    this.dropzone.on('removedfile', function (file) {
      vm.$emit('dropzone-removed-file', file)

      if (file.manuallyAdded) vm.dropzone.options.maxFiles++
    })
    this.dropzone.on('success', function (file, response) {
      vm.$emit('dropzone-success', file, response)
    })
    this.dropzone.on('successmultiple', function (file, response) {
      vm.$emit('dropzone-success-multiple', file, response)
    })
    this.dropzone.on('error', function (file, message, xhr) {
      let errorMessage

      if (xhr.status === 404) {
        errorMessage = 'Upload destination not found.'
      } else if (xhr.status === 405) {
        errorMessage = 'Invalid upload action.'
      } else if (xhr.status === 422) {
        errorMessage = message.errors[Object.keys(message.errors)[0]][0]
      } else {
        errorMessage = message.message
      }

      file.previewElement.querySelectorAll('.dz-error-message')[0].innerHTML = errorMessage

      vm.$emit('dropzone-error', file, message, xhr)
    })
    this.dropzone.on('errormultiple', function (files, message, xhr) {
      vm.$emit('dropzone-error-multiple', files, message, xhr)
    })
    this.dropzone.on('sending', function (file, xhr, formData) {
      vm.$emit('dropzone-sending', file, xhr, formData)
    })
    this.dropzone.on('sendingmultiple', function (file, xhr, formData) {
      vm.$emit('dropzone-sending-multiple', file, xhr, formData)
    })
    this.dropzone.on('complete', function (file) {
      vm.$emit('dropzone-complete', file)
    })
    this.dropzone.on('completemultiple', function (files) {
      vm.$emit('dropzone-complete-multiple', files)
    })
    this.dropzone.on('canceled', function (file) {
      vm.$emit('dropzone-canceled', file)
    })
    this.dropzone.on('canceledmultiple', function (files) {
      vm.$emit('dropzone-canceled-multiple', files)
    })
    this.dropzone.on('maxfilesreached', function (files) {
      vm.$emit('dropzone-max-files-reached', files)
    })
    this.dropzone.on('maxfilesexceeded', function (file) {
      vm.$emit('dropzone-max-files-exceeded', file)
    })
    this.dropzone.on('processing', function (file) {
      vm.$emit('dropzone-processing', file)
    })
    this.dropzone.on('processing', function (file) {
      vm.$emit('dropzone-processing', file)
    })
    this.dropzone.on('processingmultiple', function (files) {
      vm.$emit('dropzone-processing-multiple', files)
    })
    this.dropzone.on('uploadprogress', function (file, progress, bytesSent) {
      vm.$emit('dropzone-upload-progress', file, progress, bytesSent)
    })
    this.dropzone.on('totaluploadprogress', function (totaluploadprogress, totalBytes, totalBytesSent) {
      vm.$emit(
        'dropzone-total-upload-progress',
        totaluploadprogress,
        totalBytes,
        totalBytesSent
      )
    })
    this.dropzone.on('reset', function () {
      vm.$emit('dropzone-reset')
    })
    this.dropzone.on('queuecomplete', function () {
      vm.$emit('dropzone-queue-complete')
    })
    this.dropzone.on('drop', function (event) {
      vm.$emit('dropzone-drop', event)
    })
    this.dropzone.on('dragstart', function (event) {
      vm.$emit('dropzone-drag-start', event)
    })
    this.dropzone.on('dragend', function (event) {
      vm.$emit('dropzone-drag-end', event)
    })
    this.dropzone.on('dragenter', function (event) {
      vm.$emit('dropzone-drag-enter', event)
    })
    this.dropzone.on('dragover', function (event) {
      vm.$emit('dropzone-drag-over', event)
    })
    this.dropzone.on('dragleave', function (event) {
      vm.$emit('dropzone-drag-leave', event)
    })

    vm.$emit('dropzone-mounted')
  },
  beforeDestroy() {
    this.dropzone.destroy()
  },
  methods: {
    setOption(option, value) {
      this.dropzone.options[option] = value
    },
    removeAllFiles(bool) {
      this.dropzone.removeAllFiles(bool)
    },
    processQueue() {
      const dropzoneEle = this.dropzone

      this.dropzone.processQueue()

      this.dropzone.on('success', function () {
        dropzoneEle.options.autoProcessQueue = true
      })
      this.dropzone.on('queuecomplete', function () {
        dropzoneEle.options.autoProcessQueue = false
      })
    },
    init() {
      return this.dropzone.init()
    },
    destroy() {
      return this.dropzone.destroy()
    },
    updateTotalUploadProgress() {
      return this.dropzone.updateTotalUploadProgress()
    },
    getFallbackForm() {
      return this.dropzone.getFallbackForm()
    },
    getExistingFallback() {
      return this.dropzone.getExistingFallback()
    },
    setupEventListeners() {
      return this.dropzone.setupEventListeners()
    },
    removeEventListeners() {
      return this.dropzone.removeEventListeners()
    },
    disable() {
      return this.dropzone.disable()
    },
    enable() {
      return this.dropzone.enable()
    },
    filesize(size) {
      return this.dropzone.filesize(size)
    },
    accept(file, done) {
      return this.dropzone.accept(file, done)
    },
    addFile(file) {
      return this.dropzone.addFile(file)
    },
    removeFile(file) {
      this.dropzone.removeFile(file)
    },
    getAcceptedFiles() {
      return this.dropzone.getAcceptedFiles()
    },
    getRejectedFiles() {
      return this.dropzone.getRejectedFiles()
    },
    getFilesWithStatus() {
      return this.dropzone.getFilesWithStatus()
    },
    getQueuedFiles() {
      return this.dropzone.getQueuedFiles()
    },
    getUploadingFiles() {
      return this.dropzone.getUploadingFiles()
    },
    getAddedFiles() {
      return this.dropzone.getAddedFiles()
    },
    getActiveFiles() {
      return this.dropzone.getActiveFiles()
    },
  },
}
</script>
